<template>
	<vx-card title="" no-shadow>
		<div class="vx-row">
			<div class="vx-col w-full lg:w-2/3">
				<here-map
					name="map-iot"
					ref="moduleMap"
					latitude="4.686699"
					longitude="-74.0837187"
					zoom="13"
					width="100%"
					height="70vh"
					theme="uber"
					calledFrom="calendar"
					:changeCursorEnable="true"
					:getCallbackClick="getCallbackClick"
				></here-map>
			</div>
			<div class="vx-col w-full lg:w-1/3">
				<p class="text-2xl font-semibold mb-1">{{ $t("Waypoint.WaypointList") }}</p>
				<p v-if="waypoints.length > 0">{{ $t("Waypoint.ReorderWaypointsText") }}</p>

				<div class="vx-row mt-4 mb-4">
					<div class="vx-col w-full">
						<vs-button
							class="w-full"
							@click="openWaypointDialogFormGeo"
							icon-pack="feather"
							icon="icon-map-pin"
							:title="$t('Waypoint.AddWaypoints')"
							>{{ $t("Crud.Add") }}
						</vs-button>
					</div>
				</div>

				<vs-divider class="mb-base" />

				<vue-perfect-scrollbar class="scroll-area--waypoints-content" :settings="settings">
					<WaypointList
						v-if="waypoints.length > 0"
						:waypoints="waypoints"
						:action="handleActionWaypointList"
					/>

					<p v-if="waypoints.length === 0">
						<span class="font-semibold">{{ this.$t("Waypoint.NotWaypointAdded") }}. </span>
						{{ this.$t("Waypoint.AddWaypointText") }}
					</p>
				</vue-perfect-scrollbar>
			</div>
		</div>

		<WaypointDialogForm
			v-if="activePrompt"
			:wp-data="waypoint"
			:activePrompt="activePrompt"
			:is-edit="editWaypoint"
			:origin="waypointDialogFormOrigin"
			@close="handleClose"
			@cancel="handleCancel"
			@accept="handleAccept"
		/>
	</vx-card>
</template>

<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import "flatpickr/dist/flatpickr.css";
import vSelect from "vue-select";
import HereMap from "../Maps/Maps";
import moduleSchedule from "@/store/schedule/moduleSchedule.js";
import moduleVisitPoint from "@/store/visit-point/moduleVisitPoint.js";
import moduleCommon from "@/store/common/moduleCommon.js";
import WaypointList from "./WaypointList.vue";
import Waypoint from "../../models/Waypoint.js";
import WaypointDialogForm from "./WaypointDialogForm.vue";
import {generateInfowindowContentVisitPoints as ifContent} from "../Maps/infowindowContent.js";
import {addMarkerHereMap, removeMarker, removePolyline, clearInformationOnMapControl} from "@/utils/HereMap";
import {
	addInfoBubble,
	centerHereMap,
	displayInformationOnMapControl,
	drawPolyline,
	getHereMapObj,
} from "../../utils/HereMap.js";

export default {
	props: {
		dataWaypoints: {
			type: Array,
			default: () => [],
		},
		isEdit: {
			type: Boolean,
			default: false,
		},
		visitPoint: {
			type: Object,
			default: null,
		},
	},
	components: {
		vSelect,
		VuePerfectScrollbar,
		HereMap,
		WaypointList,
		WaypointDialogForm,
	},
	data() {
		return {
			// perfectscrollbar settings
			settings: {
				maxScrollbarLength: 60,
				wheelSpeed: 0.6,
			},
			markers: [],
			markersWaypoints: [],
			markerPositions: [],
			waypoints: [],
			activePrompt: false,
			location: null,
			waypoint: new Waypoint({}),
			editWaypoint: false,
			edit: this.isEdit,
			polyline: null,
			polylineList: [],
			waypointDialogFormOrigin: "geo",
		};
	},
	computed: {},
	methods: {
		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},

		openWaypointDialogFormGeo() {
			this.waypointDialogFormOrigin = "geo";
			this.waypoint = new Waypoint({});
			this.activePrompt = true;
		},

		/**
		 * Handles the click event on the map and updates the location and waypoint.
		 *
		 * @param {Object} event - The event object from the map click.
		 * @param {Function} event.lat - Function to get the latitude from the event.
		 * @param {Function} event.lng - Function to get the longitude from the event.
		 */
		getCallbackClick(evt) {
			this.waypointDialogFormOrigin = "map";
			// Obtener la ubicación en coordenadas geográficas (lat, lng)
			const coord = getHereMapObj().screenToGeo(evt.currentPointer.viewportX, evt.currentPointer.viewportY);
			// Asignar la ubicación a tu modelo
			this.location = {cy: coord.lat, cx: coord.lng};
			// Suponiendo que `waypoint` tiene métodos setLatitude y setLongitude
			this.waypoint.setLatitude(this.location.cy).setLongitude(this.location.cx);
			// Activar algún tipo de prompt visual
			this.activePrompt = true;
		},
		/**
		 * Translates the job type to a localized string and converts it to uppercase.
		 *
		 * @param {string} [jobType="PIUCKUP"] - The type of job, either "PICKUP" or any other value for "DELIVERY".
		 * @returns {string} - The translated and uppercased job type.
		 */
		jobTypeTranslate(jobType = "PIUCKUP") {
			return jobType === "PICKUP"
				? this.$t("Waypoint.Pickup").toUpperCase()
				: this.$t("Waypoint.Delivery").toUpperCase();
		},

		/**
		 * Generates the HTML content for an info window based on the waypoint data.
		 *
		 * @param {Object} wp - The waypoint object containing data to be displayed.
		 * @param {string} wp.job_type - The type of job associated with the waypoint.
		 * @param {string} wp.service_time - The service time associated with the waypoint.
		 * @returns {string} - The HTML string representing the content of the info window.
		 */
		generateInfoWindowContent(wp) {
			return `<div class="info-window">
						<div class="info-window-title">${this.jobTypeTranslate(wp.job_type)}</div>
						<div class="info-window-content">
							<div class="info-window-content-item">
								<span class="info-window-content-item-label">${this.$t("Waypoint.service_time")}:</span>
								<span class="info-window-content-item-value">${wp.service_time}</span>
							</div>
						</div>
					</div>`;
		},
		addIconSvgWithLabel(text, color, colorText = "black") {
			const template = [
				'<?xml version="1.0"?>',
				'<svg width="30px" height="30px" viewBox="0 0 100 100" version="1.1" xmlns="http://www.w3.org/2000/svg">',
				`<circle stroke="#96081f" fill="#${color}" cx="50" cy="50" r="35"/>`,
				`<text x="50%" y="50%" font-size="30pt" font-family="Arial" font-weight="bold" text-anchor="middle" fill="${colorText}" dy=".3em">` +
					text +
					"</text>" +
					"</svg>",
			].join("\n");
			return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(template);
		},
		/**
		 * Adds a marker to the map at the specified latitude and longitude with the given waypoint information.
		 *
		 * @param {number} lat - The latitude of the marker.
		 * @param {number} lng - The longitude of the marker.
		 * @param {Object} wp - The waypoint information object.
		 * @param {string} wp.job_type - The job type of the waypoint.
		 * @param {number} wp.order - The order of the waypoint.
		 *
		 * The marker options include:
		 * - drag: Enables dragging of the marker.
		 * - icon: The icon for the marker (currently null).
		 * - title: The translated job type of the waypoint.
		 * - info: The waypoint information.
		 * - infoWindow: The content for the info window of the marker.
		 * - label: The label for the marker with the waypoint order.
		 * - onDragend: Event handler for when the marker dragging ends, updating the waypoint coordinates.
		 * - onClick: Event handler for when the marker is clicked, logging the job type and coordinates.
		 *
		 * The marker's position is also stored in the markerPositions array.
		 */
		addMarker(lat, lng, wp) {
			let icon = this.addIconSvgWithLabel("" + wp.order, "#96081f", "#FFFFFF");
			// Opciones
			let options = {
				drag: true,
				icon,
				title: this.jobTypeTranslate(wp.job_type),
				infoLatLng: {lat, lng},
				info: {...wp},
				infoWindow: {
					content: this.generateInfoWindowContent(wp),
				},
				label: {
					text: "" + wp.order,
					color: "#FFFFFF",
					fontSize: "11px",
					fontFamily: "Arial",
					fontWeight: "bold",
				},
				onDragend: (e, coords) => {
					const index = this.waypoints.findIndex((w) => w.order === e.info.order);
					if (index < 0) return;
					this.markerPositions[index] = {lat: coords.lat, lng: coords.lng};
					this.waypoints[index].setLatitude(coords.lat).setLongitude(coords.lng);
					this.updateScheduleWaypoint(this.waypoints[index]);
					this.fetchWaypointsRoute();
				},
			};
			const marker = addMarkerHereMap(lat, lng, options, true);
			addInfoBubble(marker, options.infoWindow.content);
			this.markers.push(marker);
			this.markerPositions.push({lat, lng});
		},
		addVisitPointMarker(lat, lng, vp) {
			let icon = "https://maps.google.com/mapfiles/ms/icons/green-dot.png";
			let options = {
				icon,
				title: vp.name,
				info: {
					...vp,
				},
				infoWindow: {
					content: ifContent(vp),
				},
				label: null,
			};
			const marker = addMarkerHereMap(lat, lng, options, true);
			centerHereMap(lat, lng, {zoom: 13});
		},

		openDeleteConfirm(index) {
			const wpToRemove = this.waypoints[index];
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: this.$t("Record.DeleteRecord"),
				text:
					this.$t("Record.DeleteRecordConfirmText") +
					" " +
					this.jobTypeTranslate(wpToRemove.job_type) +
					" #" +
					wpToRemove.order +
					" - " +
					wpToRemove.service_time +
					" ",
				accept: () => this.removeWaypoint(index),
				acceptText: this.$t("Accept"),
				cancelText: this.$t("Cancel"),
			});
		},

		handleChanges(action = "", data) {
			if (this.isEdit) {
				switch (action) {
					case "add":
						this.createScheduleWaypoint(data);
						break;
					case "delete":
						this.deleteScheduleWaypoint(data);
						break;
					case "reorder":
						this.updateScheduleWaypointsOrder();
						break;
					case "edit":
						this.updateScheduleWaypoint(data);
						break;
					default:
						break;
				}
			} else {
				setTimeout(() => {
					this.$emit(
						"handleChanges",
						this.waypoints.map((w) => w)
					);

					if (this.waypoints.length > 0) this.fetchWaypointsRoute();
					if (this.waypoints.length === 0) {
						this.removeAllPolyline();
					}
				}, 200);
			}
		},

		/**
		 * Handles actions related to the waypoint list.
		 *
		 * @param {string} action - The action to be performed. Possible values are "edit", "delete", "select", and "reorder".
		 * @param {number} payload - The index or identifier related to the action.
		 */
		handleActionWaypointList(action, payload) {
			switch (action) {
				case "edit":
					this.waypoint = this.waypoints[payload];
					this.editWaypoint = true;
					this.activePrompt = true;
					break;
				case "delete":
					this.openDeleteConfirm(payload);
					break;
				case "select":
					const markerFound = this.markers[payload];
					this.animateMarker(markerFound);
					break;
				case "reorder":
					this.reorderMarkers(payload);
					this.handleChanges("reorder");

					break;
				default:
					break;
			}
		},
		removeWaypoint(index) {
			this.removeMarker(index);
			const wpRemoved = this.waypoints.splice(index, 1);
			this.reorderMarkers(this.waypoints);
			this.handleChanges("delete", wpRemoved[0]);
		},

		/**
		 * TODO: Removes the marker from the map and the marker array.
		 *
		 * @param {Number} index - The index of the marker to remove.
		 * @returns {void}
		 *
		 */
		removeMarker(index) {
			removeMarker(this.markers[index]);
			this.markers.splice(index, 1);
			this.markerPositions.splice(index, 1);
		},

		/**
		 * TODO: Reorders the markers on the map and the waypoints array.
		 *
		 * @param {Array<Waypoint>} payload - The new order of the waypoints.
		 * @returns {void}
		 *
		 */
		reorderMarkers(payload) {
			this.clearMarkers();
			payload.map((p, index) => {
				const waypoint = new Waypoint(p);
				waypoint.setOrder(index + 1);
				Object.assign(this.waypoints[index], waypoint);
				this.addMarker(waypoint.cy, waypoint.cx, waypoint);
				return waypoint;
			});
		},

		/**
		 * TODO: Clears all the markers from the map and the marker array.
		 *
		 * @returns {void}
		 *
		 */
		clearMarkers() {
			this.markers.forEach((marker) => {
				removeMarker(marker);
			});
			this.markers = [];
			this.markerPositions = [];
		},

		/**
		 * TODO: Adds a new waypoint to the waypoints list.
		 *
		 * @param {Object} data - The data for the new waypoint.
		 * @param {number} data.cx - The x-coordinate of the waypoint.
		 * @param {number} data.cy - The y-coordinate of the waypoint.
		 * @param {string} data.name - The name of the waypoint.
		 * @param {string} data.description - The description of the waypoint.
		 *
		 * @returns {void}
		 */
		addWaypoint(data) {
			// add the waypoint to the waypoints array if the list is less than 5
			if (this.waypoints.length >= 5) {
				this.$vs.notify({
					color: "danger",
					title: this.$t("Waypoint.Waypoint"),
					text: this.$t("Waypoint.MaxWaypointsReached"),
				});
				return;
			}

			const order = this.waypoints.length + 1;
			const wp = new Waypoint({...data, order: order});
			this.addMarker(wp.cy, wp.cx, wp);
			this.waypoints.push(wp);
			this.handleChanges("add", wp);
		},

		/**
		 * TODO: Edits the waypoint data in the waypoints array.
		 *
		 * @param {Object} data - The data object containing the waypoint information.
		 * @param {number} data.order - The order of the waypoint to be edited.
		 */
		editWaypointData(data) {
			const wp = new Waypoint(data);
			const index = this.waypoints.findIndex((w) => w.order === data.order);
			if (index < 0) return;

			this.waypoints[index].job_type = wp.job_type;
			this.waypoints[index].service_time = wp.service_time;
			this.waypoints[index].priority = wp.priority;

			this.handleChanges("edit", this.waypoints[index]);
		},

		/**
		 * TODO: Handles the acceptance of waypoint data, either adding a new waypoint or editing an existing one.
		 *
		 * @param {Object} data - The waypoint data to be processed.
		 * @param {string} [action="add"] - The action to be performed, either "add" to add a new waypoint or any other value to edit an existing waypoint.
		 */
		handleAccept(data, action = "add") {
			if (action === "add") {
				this.addWaypoint(data);
			} else {
				this.editWaypointData(data);
			}

			this.editWaypoint = false;
			this.activePrompt = false;
		},

		/**
		 * TODO: Closes the prompt and resets the waypoint editing state.
		 *
		 * This method sets `activePrompt` to `false`, indicating that the prompt should be closed.
		 * It also sets `editWaypoint` to `false`, indicating that waypoint editing should be disabled.
		 */
		handleClose() {
			this.activePrompt = false;
			this.editWaypoint = false;
		},

		/**
		 * TODO: Handles the cancel action for the event form.
		 * - Sets `activePrompt` to `false` to close any active prompts.
		 * - Resets `editWaypoint` to `false` to indicate that waypoint editing is not active.
		 * - Creates a new `Waypoint` instance and assigns it to `waypoint` to reset the waypoint data.
		 */

		handleCancel() {
			this.activePrompt = false;
			this.editWaypoint = false;
			this.waypoint = new Waypoint({});
		},

		/**
		 * Creates a schedule waypoint.
		 *
		 * @async
		 * @param {Object} waypoint - The waypoint object to be created.
		 * @returns {Promise<void>}
		 */
		async createScheduleWaypoint(waypoint) {
			const eventId = this.$route.params.event_id;

			const payload = {
				event_id: +eventId,
				waypoints: [waypoint.getDataToCreate()],
			};

			this.openLoading();

			const response = await this.$store.dispatch("moduleSchedule/createScheduleWaypoint", payload);

			this.closeLoading();

			if (response.status === 201) {
				this.$vs.notify({
					color: "success",
					title: this.$t("Waypoint.Waypoint"),
					text: this.$t("Record.TextConfirmRecordCreate"),
				});

				this.fetchWaypointsRoute();

				return;
			}

			if (response.hasOwnProperty("error") && response.error.length > 0) {
				this.validateErrorMessages(response.error[0].message);
			}
		},

		/**
		 * Updates a schedule waypoint.
		 *
		 * @param {Object} waypoint - The waypoint object to be updated.
		 * @returns {void}
		 */
		async updateScheduleWaypoint(waypoint) {
			this.openLoading();

			const response = await this.$store.dispatch(`moduleSchedule/updateScheduleWaypoint`, waypoint);

			this.closeLoading();

			if (response.status === 200) {
				this.$vs.notify({
					color: "success",
					title: this.$t("Waypoint.Waypoint"),
					text: this.$t("Record.TextConfirmRecordUpdate"),
				});

				return;
			}

			if (response.hasOwnProperty("error") && response.error.length > 0) {
				this.validateErrorMessages(response.error[0].message);
			}
		},

		/**
		 * Deletes a schedule waypoint.
		 *
		 * @param {Object} waypoint - The waypoint object to be deleted.
		 * @returns {void}
		 */
		async deleteScheduleWaypoint(waypoint) {
			this.openLoading();

			const response = await this.$store.dispatch(`moduleSchedule/deleteScheduleWaypoint`, waypoint);

			this.closeLoading();

			if (response.status === 200) {
				this.$vs.notify({
					color: "success",
					title: this.$t("Waypoint.Waypoint"),
					text: this.$t("Record.TextConfirmRecordDelete"),
				});

				this.fetchWaypointsRoute();

				return;
			}

			if (response.hasOwnProperty("error") && response.error.length > 0) {
				this.validateErrorMessages(response.error[0].message);
			}
		},

		/**
		 * Updates the schedule waypoint with the current waypoints data.
		 *
		 * This method constructs a payload with the event ID and the waypoints data,
		 * then dispatches an action to update the schedule waypoint in the store.
		 * It shows a loading indicator while the request is in progress and hides it
		 * once the request is completed.
		 *
		 * If the update is successful (response status 201), it shows a success notification.
		 * If the response contains an error, it validates and displays the error messages.
		 *
		 * @async
		 * @returns {Promise<void>}
		 */

		async updateScheduleWaypointsOrder() {
			const eventId = this.$route.params.event_id;
			const payload = {
				event_id: +eventId,
				waypoints: this.waypoints.map((waypoint) => waypoint.getDataToReorder()),
			};

			this.openLoading();

			const response = await this.$store.dispatch("moduleSchedule/updateScheduleWaypointsOrder", payload);

			this.closeLoading();

			if (response.status === 400) {
				if (response.error && response.error.length > 0) {
					this.validateErrorMessages(response.error[0].message);
				}
				return;
			}

			if (response.status === 201) {
				this.$vs.notify({
					color: "success",
					title: this.$t("Waypoint.Waypoint"),
					text: this.$t("Waypoint.WaypointsReordered"),
				});

				this.fetchWaypointsRoute();
			}
		},

		/**
		 * Fetches the waypoints route from the server and updates the polyline on the map.
		 *
		 * This method constructs a payload with the waypoints data and dispatches an action to fetch the waypoints route.
		 * if the response is successful (status 200), removes all polylines from the map, creates a new polyline with the response data,
		 *
		 * @returns {Promise<void>}
		 */
		async fetchWaypointsRoute() {
			const wps = this.waypoints.map((wp) => ({
				longitude: Number(wp.getLongitude()),
				latitude: Number(wp.getLatitude()),
			}));
			this.openLoading();
			const payload = {
				waypoints: wps.concat({longitude: this.visitPoint.cx, latitude: this.visitPoint.cy}),
			};
			const response = await this.$store.dispatch("moduleSchedule/fetchWaypointsRoute", payload);
			this.closeLoading();
			if (response.status === 400) {
				const message = response.data.reason ? response.data.reason : "RoutingFetchError";
				this.validateErrorMessages("RoutingFetchError");
				return;
			}

			this.$vs.notify({
				color: "success",
				title: this.$t("Waypoint.Waypoint"),
				text: this.$t("Waypoint.WaypointsRouteFetched"),
			});

			// validate if the response has the required data
			if (response.data && response.data.total) {
				this.removeAllPolyline();
				response.data.segments.map((data, index) => this.displayRoute({index, ...data}));
				this.displayRouteResultMapControl(response.data.total);
			}
		},
		/**
		 * Draws the routing result on the map using the provided data.
		 *
		 * @param {Object} data - The routing result data.
		 * @param {string} data.ETA - The estimated time of arrival.
		 * @param {number} data.distance_mts - The total distance in meters.
		 * @param {string} data.duration - The duration of the route.
		 * @param {Array} data.polyline - The polyline path coordinates.
		 *
		 * @returns {void}
		 */
		displayRoute(data) {
			const {ETA, distance_mts, duration, polyline, index} = data;
			const distance = isNaN(distance_mts) ? 0 : (distance_mts / 1000).toFixed(2);
			const options = {
				strokeColor: "#1A73E8",
				strokeOpacity: 0.8,
				strokeWeight: 5,
			};
			this.polyline = drawPolyline(polyline, options);
			this.polylineList.push(this.polyline);

			// Crear contenido del infoWindow (burbuja)
			const bubbleContent = `
                <div>
                    <p><strong>ETA:</strong> ${ETA}</p>
                    <p><strong>${this.$t("Routing.DistanceRoute")}:</strong> ${distance} Km</p>
                    <p><strong>${this.$t("Routing.MinimumRouteTime")}:</strong> ${duration}</p>
                    <p><strong>${this.$t("Routing.Segment")} #:</strong> ${index + 1}</p>
                </div>
            `;

			// Mostrar InfoBubble al hacer clic sobre la ruta
			this.polyline.addEventListener("tap", (evt) => {
				const bubble = new H.ui.InfoBubble(evt.target.getGeometry(), {
					content: bubbleContent,
				});
				getHereMapObj().ui.addBubble(bubble);
			});
		},
		/**
		 * Displays the route result information on the map control.
		 *
		 * @param {Object} data - The route result data.
		 * @param {string} data.ETA - The estimated time of arrival.
		 * @param {number} data.distance_mts - The total distance in meters.
		 * @param {string} data.duration - The total duration of the route.
		 *
		 * @description
		 * This method takes the route result data, formats the distance to kilometers,
		 * and creates an HTML string to display the route information on the map control.
		 * It uses the here library to display the information at the top left of the map.
		 */
		displayRouteResultMapControl(data) {
			const {ETA, distance_mts, duration} = data;
			const distance = isNaN(distance_mts) ? 0 : (distance_mts / 1000).toFixed(2);
			const innerHTML = `
				  <div>
					<h3>${this.$t("Routing.RouteResult.Title")}</h3>
					<p><strong>ETA:</strong> ${ETA}</p>
					<p><strong>${this.$t("Routing.RouteResult.TotalDistance")}:</strong> ${distance} Km</p>
					<p><strong>${duration}</strong> ${this.$t("Routing.RouteResult.RoutesTime")}</p>
				  </div>
				`;
			displayInformationOnMapControl(innerHTML, "TOP_LEFT");
		},

		/**
		 * Removes all polylines from the map.
		 *
		 * This method iterates through the `polylineList` array and sets the map of each polyline to `null`,
		 * effectively removing them from the map. After all polylines are removed, it clears the `polylineList` array.
		 */
		removeAllPolyline() {
			if (this.polylineList.length > 0) {
				this.polylineList.forEach((poly) => {
					removePolyline(poly);
				});
			}
			this.polylineList = [];

			// remove the route result information from the map control
			clearInformationOnMapControl("TOP_LEFT");
		},

		validateErrorMessages(message) {
			let text = this.$t("ErrorMessage");
			switch (message) {
				case "An error occurred while processing the waypoints in Serviroutes.":
					text = this.$t("Waypoint.ServiroutesErrorCreate");
					break;
				case "init_hour less than current time":
					text = this.$t("Schedule.ErrorProcess.InitHourError");
					this.init_hour_error = true;
					break;
				case "visit point is not related to resource":
					text = this.$t("Schedule.ErrorProcess.VisitpointError");
					break;
				case "resource has no activities":
					text = this.$t("Schedule.ErrorProcess.ResourceActivityError");
					break;
				case "RoutingFetchError":
					text = this.$t("Waypoint.RoutingFetchError");
					break;
				default:
					text = message;
					break;
			}
			this.$vs.notify({
				time: 4000,
				color: "danger",
				title: this.$t("ErrorTitle"),
				text,
			});
		},
	},

	created() {
		if (!moduleSchedule.isRegistered) {
			this.$store.registerModule("moduleSchedule", moduleSchedule);
			moduleSchedule.isRegistered = true;
		}

		if (!moduleVisitPoint.isRegistered) {
			this.$store.registerModule("moduleVisitPoint", moduleVisitPoint);
			moduleVisitPoint.isRegistered = true;
		}

		if (!moduleCommon.isRegistered) {
			this.$store.registerModule("moduleCommon", moduleCommon);
			moduleCommon.isRegistered = true;
		}
	},
	mounted() {
		if (this.isEdit) {
			this.waypoints = this.dataWaypoints.map((wp) => {
				this.addMarker(wp.cy, wp.cx, wp);
				return new Waypoint(wp);
			});

			setTimeout(() => {
				if (this.waypoints.length > 0) this.fetchWaypointsRoute();
			}, 500);
		}

		// add visit point marker to the map if it exists
		if (this.visitPoint) {
			setTimeout(() => {
				this.addVisitPointMarker(this.visitPoint.cy, this.visitPoint.cx, this.visitPoint);
			}, 1000);
		}
	},
	beforeDestroy() {},
};
</script>

<style lang="scss" scoped>
.vs-popup--content-custom {
	.vs-popup--content {
		height: 100%;
	}

	.btn-save {
		position: absolute;
		left: cal(100 vw- 10%);
		bottom: 30px;
	}

	.btn-del {
		position: absolute;
		left: 200px;
		bottom: 30px;
	}
}

.scroll-area--waypoints-content {
	margin: auto;
	width: 100%;
	height: 350px;
}
</style>
